import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner, Switch } from '../../../components';
import { staffService } from '../../staff/services/staffService';

/**
 * Per-staff notification preferences. Lives under Settings → Notifications
 * and is visible to every logged-in staff (unlike the other Settings tabs
 * which are store-level / admin-gated).
 */
export default function NotificationSettings() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data: me, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => staffService.getMe(),
  });

  const updateMutation = useMutation({
    mutationFn: (notifyOnNewSession: boolean) =>
      staffService.updateMyPreferences({ notifyOnNewSession }),
    onSuccess: (next) => {
      queryClient.setQueryData(['me'], next);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message || 'Could not save your preference. Please try again.');
    },
  });

  if (isLoading || !me) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Spinner text="Loading your preferences..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-lg font-semibold text-ink">Notifications</h2>
        <p className="mt-1 text-sm text-ink-2">
          Choose when Pulse should email you. These settings only affect your
          own account.
        </p>
      </div>

      <div className="rounded-panel border border-line bg-surface p-4 shadow-s">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium text-ink">
              Email me when a new chat starts
            </label>
            <p className="mt-1 text-xs text-ink-3">
              Whenever a customer opens a new conversation on any channel
              (WhatsApp, web widget, voice), Pulse will email{' '}
              <span className="font-medium text-ink">{me.email}</span>{' '}
              with a link to jump straight to the chat.
            </p>
          </div>
          <Switch
            checked={me.notifyOnNewSession}
            disabled={updateMutation.isPending}
            ariaLabel="Email me when a new chat starts"
            onChange={(next) => updateMutation.mutate(next)}
          />
        </div>
      </div>

      {error && <p className="text-sm text-danger">{error}</p>}
    </div>
  );
}
